import { useState, useCallback, useEffect, useMemo } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'

import { selectCsvUploaderStatus, uploadCsvFile } from 'slices/csvUploaderSlice'
import { ENABLE_DIALOG_ERROR_STATUS_CODES } from 'slices/utils'

import { CustomModal, FileInput, SelectBoxFormat } from 'components/common'
import { COLUMN_SIZES } from 'components/common/constants'

import type { LoadFilePropsWithoutEncoding } from 'hooks/useImportCsv'
import { useImportCsv } from 'hooks/useImportCsv'

type Props = {
  isOpen: boolean
  onSuccess: () => void
  onCancel: () => void
}

const ENCODING_ERROR_MESSAGE = 'SHIFT-JIS形式のファイルを選択してください。'

/**
 * ファイルがSHIFT-JIS以外と判定できる場合に true を返す。
 * - UTF-8 BOM / UTF-16 BOM が存在する場合は確実にNG
 * - UTF-8として厳密デコードに成功し、かつ非ASCIIバイトがある場合はUTF-8と判定
 * 100%の検出は不可能なため、ベストエフォートの検出。
 */
const detectNonShiftJis = (buffer: ArrayBuffer): boolean => {
  const bytes = new Uint8Array(buffer)

  // UTF-8 BOM (EF BB BF)
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return true
  }

  // UTF-16 BOM (FF FE = LE, FE FF = BE)
  if ((bytes[0] === 0xff && bytes[1] === 0xfe) || (bytes[0] === 0xfe && bytes[1] === 0xff)) {
    return true
  }

  // UTF-8として厳密デコードを試みる
  try {
    new TextDecoder('utf-8', { fatal: true }).decode(buffer)
    // デコード成功 かつ 非ASCIIバイトあり → UTF-8の可能性が高い
    const hasNonAscii = bytes.some(b => b > 0x7f)
    return hasNonAscii
  } catch {
    // デコード失敗 → SHIFT-JIS等の可能性あり
    return false
  }
}

export const CsvAutoRecordsUploadDialog = ({ isOpen, onSuccess, onCancel }: Props) => {
  const [selectedBucketDirectory, setSelectedBucketDirectory] = useState<string>()
  const [selectedDataSourceName, setSelectedDataSourceName] = useState<string>()
  const [submitted, setSubmitted] = useState(false)
  const [encodingErrorMessage, setEncodingErrorMessage] = useState<string | undefined>(undefined)
  const [fileInputKey, setFileInputKey] = useState(0)

  const dispatch = useDispatch()
  const { buckets, isRequesting, errorMessage } = useSelector(selectCsvUploaderStatus, shallowEqual)

  const { setFile, importDisabled, importCsvFileWithOutEncoding, modalErrorMessage, setModalErrorMessage } =
    useImportCsv()

  const bucketItems = useMemo(() => buckets.map(b => ({ key: b.bucketDirectory, value: b.name })), [buckets])

  useEffect(() => {
    if (!submitted || isRequesting) {
      return
    }
    if (errorMessage === '') {
      onSuccess()
    } else if (!ENABLE_DIALOG_ERROR_STATUS_CODES.includes(errorMessage)) {
      setModalErrorMessage('エラーが発生しました。')
    }
    setSubmitted(false)
  }, [submitted, isRequesting, errorMessage, onSuccess, setModalErrorMessage])

  useEffect(() => {
    if (!isOpen) {
      setEncodingErrorMessage(undefined)
      setFileInputKey(k => k + 1)
      setFile(null)
      setModalErrorMessage(undefined)
    }
  }, [isOpen, setFile, setModalErrorMessage])

  const handleFileChange = useCallback(
    (f: File | null) => {
      if (!f) {
        setFile(null)
        return
      }

      const reader = new FileReader()
      reader.onload = e => {
        const buffer = e.target?.result
        if (!(buffer instanceof ArrayBuffer)) {
          setFile(f)
          return
        }
        if (detectNonShiftJis(buffer)) {
          setFile(null)
          setEncodingErrorMessage(ENCODING_ERROR_MESSAGE)
          setFileInputKey(k => k + 1)
        } else {
          setFile(f)
          setEncodingErrorMessage(undefined)
        }
      }
      reader.onerror = () => setFile(f) // 読み取り失敗時はサーバー側に委ねる
      reader.readAsArrayBuffer(f)
    },
    [setFile]
  )

  const handleApprove = useCallback(() => {
    if (!selectedBucketDirectory || !selectedDataSourceName) {
      return
    }
    const handleFileLoad = (data: LoadFilePropsWithoutEncoding) => {
      dispatch(uploadCsvFile(selectedBucketDirectory, selectedDataSourceName, data.content))
      setSubmitted(true)
    }
    importCsvFileWithOutEncoding(handleFileLoad)
  }, [dispatch, importCsvFileWithOutEncoding, selectedBucketDirectory, selectedDataSourceName])

  return (
    <CustomModal
      isOpen={isOpen}
      title="作業実績アップロード"
      approveLabel="アップロード"
      errorMessage={modalErrorMessage}
      onCancel={onCancel}
      onApprove={handleApprove}
      onHideNotification={() => setModalErrorMessage(undefined)}
      submitName="csv-auto-records-upload-submit"
      approveDisabled={importDisabled || !selectedBucketDirectory}
    >
      <div className="d-flex flex-column row-gap-3">
        <div>事前に設定されたデータフォーマットでCSVアップロードをしてください。</div>
        <div className="d-flex flex-column row-gap-4">
          <SelectBoxFormat
            label="アップロード先名称"
            placeholder="アップロード先を選択"
            value={selectedBucketDirectory}
            size={COLUMN_SIZES.MIDDLE}
            items={bucketItems}
            onChange={item => {
              setSelectedBucketDirectory(item.key?.toString())
              setSelectedDataSourceName(item.value)
            }}
          />
          <div>
            <FileInput key={fileInputKey} id="csv-auto-records-upload" accept=".csv" onChange={handleFileChange} />
            {encodingErrorMessage && <div className="text-danger mt-1">{encodingErrorMessage}</div>}
          </div>
        </div>
      </div>
    </CustomModal>
  )
}
